import { useCallback, useRef, useEffect } from 'react';

import { TFunction } from 'i18next';
import {
  PlayIcon,
  ArrowLeftIcon,
  FileTextIcon,
  GlobeIcon,
  BookOpenTextIcon,
  MessagesSquareIcon,
} from 'lucide-react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router';
import { useTitle } from 'react-use';
import { toast } from 'sonner';

import { DetailsPanelToggle } from '@/components/atoms/DetailsPanelToggle/DetailsPanelToggle';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { MODAL_AI_AGENT_CREATE, MODAL_TRYIT } from '@/components/organisms/Modals/ModalConductor';
import {
  PageSubHeader,
  PageSubHeaderActions,
  PageSubHeaderBreadcrumbs,
  PageSubHeaderTabs,
} from '@/components/organisms/PageSubHeader';
import { Sidebar } from '@/components/organisms/Sidebar/Sidebar';
import { SidebarLayout } from '@/components/organisms/Sidebar/SidebarLayout';
import { Button } from '@/components/ui/button';
import { ShortcutHint } from '@/components/ui/kbd';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SHORTCUTS } from '@/constants/shortcuts';
import useBrains from '@/hooks/useBrains';
import useCollections from '@/hooks/useCollections';
import { useDirtyFormBlocker } from '@/hooks/useDirtyFormBlocker';
import { useSetDefaultSidebar } from '@/hooks/useSetDefaultSidebar';
import { useSidebar } from '@/hooks/useSidebar';
import { openPublishModal } from '@/redux/collections/actions';
import { pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { setBrainId, setDatasourceId, setDocumentId } from '@/redux/session/actions';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import { resetSidebar, setDefaultSidebar, setSidebarContent } from '@/redux/sidebar/actions';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { SidebarContent, SidebarOptions } from '@/shared/types/sidebar';
import { RootState } from '@/shared/types/state';
import { TABLE_LAYOUT_FILL_CLASSES } from '@/util/constants';

import { DatasourcesDocumentsTable } from '../components/Tables/DatasourcesDocumentsTable/DatasourcesDocumentsTable';
import { useTabCategory } from '../hooks/useCategory';
import { useLastVisitedCategory } from '../hooks/useLastVisitedCategory';
import { useResetSidebarSelection } from '../hooks/useResetSidebarSelection';
import { useRTMCollection } from '../hooks/useRTMCollection';
import { DeleteDatasourceModal } from '../modals/DeleteDatasourceModal';
import { DeleteDocumentModal } from '../modals/DeleteDocumentModal';
import { mapSidebarContentToCategory } from '../utils/sidebarMapping';
import { DatasourcesDocumentsBreadcrumbs } from './breadcrumbs/DatasourcesDocumentsBreadcrumbs';
import {
  RIGHT_PANEL_SIDEBAR_KINDS,
  RightPanelContent,
} from '../components/RightPanel/RightPanelContent';
import DeleteCollectionModal from '../modals/DeleteCollectionModal';
import { PublishModal } from '../modals/PublishModal';

const SIDEBAR_SIZE_KEY = 'sidebar-size';

// Tab configuration for datasource navigation
const DATASOURCE_TABS = [
  { value: 'faq', icon: MessagesSquareIcon, labelKey: 'collections.tabs.faq' },
  { value: 'documents', icon: FileTextIcon, labelKey: 'collections.tabs.documents' },
  { value: 'webpages', icon: GlobeIcon, labelKey: 'collections.tabs.webpages' },
  { value: 'external', icon: BookOpenTextIcon, labelKey: 'collections.tabs.external' },
] as const;

const getKnowledgeBasePageTitle = (
  t: TFunction,
  category: string | undefined,
  collectionName: string | undefined
): string => {
  return t(`pages.kb_${category ?? 'faq'}`, { 0: collectionName ?? '-' });
};

/** Tab item that switches between text and icon-only based on the container width. */
const DatasourceTabItem = ({
  value,
  icon: Icon,
  labelKey,
}: {
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  labelKey: string;
}) => {
  const { t } = useTranslation();

  return (
    <InfoTooltip content={t(labelKey)}>
      <span className="inline-flex">
        <TabsTrigger className="h-8" value={value}>
          <Icon className="block h-4 w-4 @xl:hidden" />
          <span className="hidden @xl:inline">{t(labelKey)}</span>
        </TabsTrigger>
      </span>
    </InfoTooltip>
  );
};

// Panels that cannot render without a row of the current collection.
const ROW_ONLY_PANELS: SidebarOptions[] = ['edit_document', 'rename_folder', 'ingestion_report'];

export const DatasourcesDocumentsPage = () => {
  useSegments(PageName.COLLECTION);
  // Redux selectors
  const slug = useSelector(selectAccountSlug);
  const brainId = useSelector(selectBrainId);

  // Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { collectionId } = useParams();

  const { t } = useTranslation();
  const { collection, collections } = useCollections(collectionId);
  const { brains } = useBrains();
  const { category } = useTabCategory();

  const { setLastVisitedCategory } = useLastVisitedCategory(collectionId);

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );
  const { content: openSidebar, isOpen, open: openPanel, close: closePanel } = useSidebar();
  const isSidebarOpen = isOpen();

  useResetSidebarSelection();

  // Ref to track previous category for sidebar closing logic
  const prevCategoryRef = useRef<string | undefined>(category);
  // Ref to track previous sidebar content to map it when switching categories
  const prevSidebarContentRef = useRef<SidebarContent>(openSidebar);

  // Block the user from navigating to other routes if any form is dirty
  useDirtyFormBlocker();

  useTitle(getKnowledgeBasePageTitle(t, category, collection?.name));

  useRTMCollection(collectionId);

  // Update sidebar content when changing categories
  useEffect(() => {
    if (
      category &&
      prevCategoryRef.current &&
      category !== prevCategoryRef.current &&
      isSidebarOpen
    ) {
      // If previous sidebar was datasource_details or details, keep it
      // Otherwise, map the previous sidebar content to equivalent for new category
      const newSidebarContent = mapSidebarContentToCategory(
        prevSidebarContentRef.current,
        category
      );
      // The selection belongs to the previous tab, and the remapped add_* panel would edit it.
      dispatch(setDatasourceId(null));
      dispatch(setDocumentId(null));
      dispatch(setSidebarContent(newSidebarContent));
    }
    prevCategoryRef.current = category;
  }, [category, dispatch, isSidebarOpen]);

  // Track sidebar content changes
  useEffect(() => {
    prevSidebarContentRef.current = openSidebar;
  }, [openSidebar]);

  // Handle tab changes with URL updates for deep linking
  const handleStatusChange = useCallback(
    (tabValue: string) => {
      // Only clear filters when actually changing tabs
      const isChangingTabs = tabValue !== category;

      // Don't proceed if we're not actually changing tabs
      if (!isChangingTabs) {
        return;
      }

      // Save the new category to localStorage
      setLastVisitedCategory(tabValue);

      // Create a new URLSearchParams object with the current search parameters
      // Update URL for deep linking with the new route structure
      navigate(`/${slug}/knowledge-bases/${collectionId}/category/${tabValue}`, { replace: true });
    },
    [category, navigate, slug, collectionId, setLastVisitedCategory]
  );

  const openTryItModal = () => {
    // Check if any collection language matches any brain language
    const hasBrainLanguage = brains.some((brain) =>
      collections.some((col) => col.language === brain.language)
    );

    if (!hasBrainLanguage) {
      toast.info(t('collections.try_it.no_matching_brain_language'), {
        description: t('collections.try_it.no_matching_brain_language_description'),
        action: (
          <Button
            size="icon-xss"
            className="h-6 w-fit p-2 text-xs"
            onClick={() => {
              navigate(`/${slug}/ai-agents`);
              dispatch(pushModal(MODAL_AI_AGENT_CREATE));
            }}
          >
            {t('common.create')}
          </Button>
        ),
      });
      return;
    }
    // This is the brain id that will be used to open the try it modal
    // First try to use the current brain if it matches the collection language
    // Then try to find a brain that matches the collection language
    // Finally fall back to null if no language match is found
    const initialBrainId =
      (brainId &&
        brains.find(
          (brain) => brain.brain_id === brainId && brain.language === collection?.language
        )?.brain_id) ||
      brains.find((brain) => brain.language === collection?.language)?.brain_id ||
      null;

    dispatch(setBrainId({ brainId: initialBrainId }));

    dispatch(
      pushModal(MODAL_TRYIT, { brainId: initialBrainId, collectionLanguage: collection?.language })
    );
  };

  useHotkeys(
    SHORTCUTS.test,
    (e: KeyboardEvent) => {
      e.preventDefault();
      openTryItModal();
    },
    { enableOnFormTags: false },
    [brains, brainId, collection?.language]
  );

  useHotkeys(
    SHORTCUTS.publish,
    (e: KeyboardEvent) => {
      e.preventDefault();
      if (!canWrite) {
        return;
      }
      dispatch(openPublishModal());
    },
    { enableOnFormTags: false },
    [canWrite]
  );

  useSetDefaultSidebar('datasource_details');

  // On collection change drop a row panel inherited from another collection, as ContentPage does.
  useEffect(() => {
    if (openSidebar && ROW_ONLY_PANELS.includes(openSidebar)) {
      // resetSidebar drops the pin first; on its own setDefaultSidebar would only consume it.
      dispatch(resetSidebar());
      dispatch(setDefaultSidebar('datasource_details'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId, dispatch]);

  // Open sidebar when targetSidebar is passed in open sidebar is passed in the URL
  useEffect(() => {
    const sidebarParam = new URLSearchParams(window.location.search).get('sidebar');

    if (sidebarParam) {
      dispatch(setSidebarContent(sidebarParam as SidebarContent));
    }
  }, [dispatch]);

  const testTooltipContent = (
    <span className="inline-flex items-center gap-1.5">
      {t('collections.test_tooltip')} <ShortcutHint shortcut={SHORTCUTS.test} />
    </span>
  );

  const publishTooltipContent = (
    <span className="inline-flex items-center gap-1.5">
      {t('collections.publish_tooltip')} <ShortcutHint shortcut={SHORTCUTS.publish} />
    </span>
  );

  const detailsTooltipContent = isOpen('datasource_details')
    ? t('common.hide_details')
    : t('common.show_details');

  return (
    <div className="flex h-full w-full flex-col">
      <div className="min-h-0 flex-1">
        <SidebarLayout
          autoSaveId={SIDEBAR_SIZE_KEY}
          sidebar={
            <Sidebar title={t('collections.details')} when={RIGHT_PANEL_SIDEBAR_KINDS}>
              <RightPanelContent />
            </Sidebar>
          }
        >
          <PageSubHeader
            className="grid-cols-[minmax(0,1fr)_2fr_minmax(0,1fr)]"
            inlineTabs
            breadcrumbs={
              <PageSubHeaderBreadcrumbs>
                <div className="flex items-center">
                  <Button
                    variant="ghost-neutral"
                    size="icon-sm"
                    onClick={() => navigate(`/${slug}/knowledge-bases`)}
                  >
                    <ArrowLeftIcon size={16} />
                  </Button>
                  <div className="mr-4 ml-2 h-4 w-px bg-border" />
                  <DatasourcesDocumentsBreadcrumbs />
                </div>
              </PageSubHeaderBreadcrumbs>
            }
            tabs={
              <PageSubHeaderTabs className="@container w-full justify-center overflow-hidden">
                <Tabs
                  value={category || 'faq'}
                  onValueChange={handleStatusChange}
                  className="w-auto"
                >
                  <TabsList>
                    {DATASOURCE_TABS.map(({ value, icon, labelKey }) => (
                      <DatasourceTabItem
                        key={value}
                        value={value}
                        icon={icon}
                        labelKey={labelKey}
                      />
                    ))}
                  </TabsList>
                </Tabs>
              </PageSubHeaderTabs>
            }
            actions={
              <PageSubHeaderActions>
                <div className="flex items-center gap-2">
                  <DetailsPanelToggle
                    pressed={isOpen('datasource_details')}
                    onPressedChange={(pressed) =>
                      pressed ? openPanel('datasource_details') : closePanel()
                    }
                    tooltip={detailsTooltipContent}
                  />
                  <InfoTooltip size="large" content={testTooltipContent}>
                    <Button
                      variant="outline-neutral"
                      size="sm"
                      onClick={openTryItModal}
                      className="flex items-center"
                    >
                      <PlayIcon size={16} />
                      {t('common.test')}
                    </Button>
                  </InfoTooltip>
                  <InfoTooltip content={publishTooltipContent}>
                    <Button
                      size="sm"
                      onClick={() => dispatch(openPublishModal())}
                      disabled={!canWrite}
                    >
                      {t('collections.publish')}
                    </Button>
                  </InfoTooltip>
                </div>
              </PageSubHeaderActions>
            }
          />

          {/* Main content area */}
          <div className={TABLE_LAYOUT_FILL_CLASSES}>
            <Tabs value={category || 'faq'} className="flex h-full min-h-0 flex-col">
              <div className="flex min-h-0 flex-1 flex-col">
                <DatasourcesDocumentsTable />
              </div>
            </Tabs>
          </div>
        </SidebarLayout>
      </div>

      <DeleteDatasourceModal />

      <DeleteDocumentModal />

      <DeleteCollectionModal />

      <PublishModal />
    </div>
  );
};
